import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, CalendarRange, Wand2 } from "lucide-react";
import { workPlannerApi } from "@/services/workPlannerApi";
import { mpApi, serviceMeta, SERVICE_KEYS, statusMeta, bucketOf, isOverdue, weeksOf, weekIndexOf, istToday, isSunday, fmtDay } from "./mpShared";

/** Distribute each client's month across weeks. Drag a task to another week to move it. */
export default function PlanningTab({ month, businessId, filters, overview, members, openTask, refresh, canManage }) {
  const [tasks, setTasks] = useState(null);
  const [busy, setBusy] = useState("");
  const weeks = useMemo(() => weeksOf(month), [month]);
  const today = istToday();
  const currentWeek = today.slice(0, 7) === month ? weekIndexOf(today, month) : null;
  const memberName = useMemo(() => Object.fromEntries(members.map((m) => [m.user_id, m.name])), [members]);

  const load = useCallback(() => {
    const f = filters;
    return mpApi("/tasks", { params: { month, business_id: businessId || undefined, client_id: f.client_id, service: f.service, status: f.status, assigned_to: f.assigned_to, priority: f.priority, overdue: f.overdue ? "1" : "", search: f.search } })
      .then((d) => setTasks(d.tasks)).catch((e) => { toast.error(e.message); setTasks([]); });
  }, [month, businessId, filters]);
  useEffect(() => { load(); }, [load]);

  const clients = useMemo(() => {
    const ids = new Set((tasks || []).map((t) => t.client_id));
    return (overview?.clients || []).filter((c) => (ids.has(c.client_id) || c.target_total > 0) && (!filters.client_id || c.client_id === filters.client_id));
  }, [overview, tasks, filters.client_id]);

  const grid = useMemo(() => {
    const g = {};
    for (const t of tasks || []) {
      const w = weekIndexOf(t.due_date, month) || 0; // 0 = unscheduled
      const key = `${t.client_id}|${w}`;
      (g[key] = g[key] || []).push(t);
    }
    return g;
  }, [tasks, month]);

  /** Move keeping the same position inside the week where possible; never onto a Sunday. */
  const targetDay = (task, week) => {
    const from = Number(week.from.slice(8)), to = Number(week.to.slice(8));
    const oldDay = task.due_date ? Number(String(task.due_date).slice(8, 10)) : null;
    let day = oldDay ? from + ((oldDay - 1) % 7) : from;
    if (day > to) day = to;
    let key = `${month}-${String(day).padStart(2, "0")}`;
    if (isSunday(key)) key = `${month}-${String(day > from ? day - 1 : Math.min(to, day + 1)).padStart(2, "0")}`;
    return key;
  };

  const moveToWeek = async (taskId, weekIndex) => {
    const t = (tasks || []).find((x) => (x.task_id || x._id) === taskId);
    const week = weeks[weekIndex - 1];
    if (!t || !week || weekIndexOf(t.due_date, month) === weekIndex) return;
    const day = targetDay(t, week);
    setTasks((prev) => prev.map((x) => ((x.task_id || x._id) === taskId ? { ...x, due_date: day, start_date: day } : x)));
    try { await workPlannerApi.updateTask(taskId, { due_date: day, start_date: day }); toast.success(`Moved to ${week.label} (${fmtDay(day)})`); refresh(); }
    catch (e) { toast.error(e.message); load(); }
  };

  /** Spread a client's unfinished tasks evenly over the remaining working days. */
  const autoSchedule = async (client) => {
    const open = (tasks || []).filter((t) => t.client_id === client.client_id && bucketOf(t.status) !== "completed");
    if (!open.length) return toast.info("Nothing left to schedule for this client.");
    const start = today.slice(0, 7) === month ? Number(today.slice(8)) : 1;
    const total = Number(weeks[weeks.length - 1].to.slice(8));
    const days = [];
    for (let d = start; d <= total; d += 1) { const k = `${month}-${String(d).padStart(2, "0")}`; if (!isSunday(k)) days.push(k); }
    if (!days.length) return toast.error("No working days left this month.");
    // Round-robin services so each week gets a mix, not all creatives first.
    const ordered = [];
    const bySvc = SERVICE_KEYS.concat([""]).map((k) => open.filter((t) => (SERVICE_KEYS.includes(t.service) ? t.service : "") === k));
    while (bySvc.some((l) => l.length)) for (const l of bySvc) if (l.length) ordered.push(l.shift());
    setBusy(client.client_id);
    try {
      for (let i = 0; i < ordered.length; i += 1) {
        const day = days[Math.min(days.length - 1, Math.floor(((i + 0.5) / ordered.length) * days.length))];
        if (String(ordered[i].due_date || "").slice(0, 10) !== day) await workPlannerApi.updateTask(ordered[i].task_id || ordered[i]._id, { due_date: day, start_date: day });
      }
      toast.success(`${ordered.length} task(s) spread across the month for ${client.name}`);
      await load(); refresh();
    } catch (e) { toast.error(e.message); load(); } finally { setBusy(""); }
  };

  const summarize = (list) => {
    const counts = {};
    for (const t of list) { const k = SERVICE_KEYS.includes(t.service) ? t.service : "other"; counts[k] = (counts[k] || 0) + 1; }
    return Object.entries(counts).map(([k, n]) => `${n} ${k === "other" ? "Other" : serviceMeta(k).short}`).join(" + ");
  };

  const columns = [...weeks, { index: 0, label: "Unscheduled", range: "" }];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500"><CalendarRange className="mr-1 inline h-4 w-4" />Drag a task card between weeks to reschedule it. Each cell summarises the week's mix.</p>
        {tasks === null && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[1100px] border-collapse text-sm">
          <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="sticky left-0 z-10 w-48 bg-slate-50 px-3 py-2.5">Client</th>
              {columns.map((w) => (
                <th key={w.index} className={`px-2 py-2.5 ${w.index === currentWeek ? "text-indigo-700" : ""}`}>
                  {w.label} {w.range && <span className="font-normal normal-case text-slate-400">({w.range})</span>}
                  {w.index === currentWeek && <span className="ml-1 rounded bg-indigo-100 px-1 text-[10px] normal-case text-indigo-700">this week</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="py-12 text-center text-slate-500">No tasks this month. Use “Generate Monthly Tasks” to create them from the targets.</td></tr>
            ) : clients.map((c) => (
              <tr key={c.client_id} className="border-t border-slate-100 align-top">
                <td className="sticky left-0 z-10 bg-white px-3 py-2">
                  <div className="font-medium text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.target_total} target</div>
                  {canManage && (
                    <button onClick={() => autoSchedule(c)} disabled={!!busy} className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:underline">
                      {busy === c.client_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} Auto schedule
                    </button>
                  )}
                </td>
                {columns.map((w) => {
                  const list = grid[`${c.client_id}|${w.index}`] || [];
                  const droppable = w.index > 0;
                  return (
                    <td key={w.index}
                      onDragOver={droppable ? (e) => e.preventDefault() : undefined}
                      onDrop={droppable ? (e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/task_id"); if (id) moveToWeek(id, w.index); } : undefined}
                      className={`min-w-[160px] border-l border-slate-100 px-2 py-2 ${w.index === currentWeek ? "bg-indigo-50/30" : ""} ${w.index === 0 ? "bg-slate-50/60" : ""}`}>
                      {list.length > 0 && <div className="mb-1.5 text-[11px] font-medium text-slate-500">{summarize(list)}</div>}
                      <div className="space-y-1">
                        {list.map((t) => {
                          const m = serviceMeta(t.service); const done = bucketOf(t.status) === "completed"; const over = isOverdue(t);
                          return (
                            <div key={t._id} draggable onDragStart={(e) => e.dataTransfer.setData("text/task_id", t.task_id || t._id)} onClick={() => openTask(t)}
                              title={`${t.title} · ${statusMeta(t.status).label} · ${fmtDay(t.due_date)}`}
                              className={`cursor-grab rounded-md border-l-[3px] bg-white px-1.5 py-1 text-[11px] shadow-sm ring-1 ring-slate-200 hover:ring-indigo-300 active:cursor-grabbing ${done ? "opacity-55" : ""} ${over ? "ring-rose-300" : ""}`}
                              style={{ borderLeftColor: m.color }}>
                              <div className={`truncate font-medium text-slate-800 ${done ? "line-through" : ""}`}>{t.title}</div>
                              <div className="flex justify-between gap-1 text-[10px] text-slate-400">
                                <span>{fmtDay(t.due_date)}</span>
                                <span className="truncate">{(t.assigned_users || []).map((u) => memberName[u]).filter(Boolean)[0] || "—"}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
